// src/utils/invoiceUtils.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../services/supabase';

export const generateInvoiceNumber = async () => {
  const { data: existingInvoices, error } = await supabase
    .from('invoices')
    .select('invoice_number')
    .order('invoice_number', { ascending: false })
    .limit(1);
  if (error) throw new Error(`Failed to fetch existing invoices: ${error.message}`);

  const lastInvoiceNumber = existingInvoices.length > 0 ? existingInvoices[0].invoice_number : null;
  let count = 5110; // Start at 5110
  if (lastInvoiceNumber) {
    const match = lastInvoiceNumber.match(/INV-(\d+)/);
    if (match) {
      const lastNumber = parseInt(match[1], 10);
      count = Math.max(lastNumber + 1, 5110); // Ensure we start at least at 5110
    }
  }
  return `INV-${String(count).padStart(4, '0')}`; // e.g., INV-5110
};

export const generateInvoicePDF = async (deliveryData, invoiceNumber, createdAt, netDueDate) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = '#384f77';
    const white = '#ffffff';
  
    // Header
    doc.setFillColor(white);
    doc.rect(0, 0, pageWidth, 20, 'F');
    doc.setFontSize(24);
    doc.setTextColor(primaryColor);
    doc.text('INVOICE', pageWidth / 2, 14, { align: 'center' });
  
    // Invoice Number (Above Company Info, Left-Aligned)
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`Invoice: ${invoiceNumber}`, 15, 20);
  
    // Company Information
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('Advanced Controls & Automation', 15, 30);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('574 Hudson River Road', 15, 36);
    doc.text('Waterford, NY 12188', 15, 42);
    doc.text('518-369-4408', 15, 48);
    doc.text('sales@acamachine.com', 15, 54);
  
    // Add Company Logo
    const logoImg = new Image();
    logoImg.src = require('../assets/acaLogo.png');
    try {
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = () => reject(new Error('Logo image failed to load'));
        setTimeout(() => reject(new Error('Logo image load timeout')), 5000);
      });
      doc.addImage(logoImg, 'PNG', pageWidth - 45, 30, 30, 30);
    } catch (err) {
      console.error('Error loading logo:', err);
      doc.text('Logo Missing', pageWidth - 45, 45);
    }
  
    // Customer Information
    doc.setFontSize(10);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    let billTo = { name: 'N/A', address: 'N/A', city: 'N/A', state: 'N/A', zip: 'N/A', contact_name: 'N/A', contact_phone: 'N/A', contact_email: 'N/A' };
    let shipTo = { name: 'N/A', address: 'N/A', city: 'N/A', state: 'N/A', zip: 'N/A', contact_name: 'N/A', contact_phone: 'N/A', contact_email: 'N/A' };
    const customer = deliveryData.customer || {};
    billTo = {
      name: customer.nickname || 'N/A',
      address: customer.billing_address || 'N/A',
      city: customer.billing_city || 'N/A',
      state: customer.billing_state || 'N/A',
      zip: customer.billing_zip || 'N/A',
      contact_name: customer.billing_contact_name || 'N/A',
      contact_phone: customer.billing_contact_phone || 'N/A',
      contact_email: customer.billing_contact_email || 'N/A',
    };
    shipTo = {
      name: customer.nickname || 'N/A',
      address: customer.shipping_address || 'N/A',
      city: customer.shipping_city || 'N/A',
      state: customer.shipping_state || 'N/A',
      zip: customer.shipping_zip || 'N/A',
      contact_name: customer.shipping_contact_name || 'N/A',
      contact_phone: customer.shipping_contact_phone || 'N/A',
      contact_email: customer.shipping_contact_email || 'N/A',
    };
  
    // Bill To
    doc.text('Bill To:', 15, 72);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    let billToY = 78;
    doc.text(billTo.name, 15, billToY);
    if (billTo.contact_name !== 'N/A') {
      billToY += 6;
      doc.text(billTo.contact_name, 15, billToY);
    }
    if (billTo.address !== 'N/A') {
      billToY += 6;
      doc.text(billTo.address, 15, billToY);
    }
    if (billTo.city !== 'N/A' && billTo.state !== 'N/A' && billTo.zip !== 'N/A') {
      billToY += 6;
      doc.text(`${billTo.city}, ${billTo.state} ${billTo.zip}`, 15, billToY);
    } else {
      const cityStateZip = [
        billTo.city !== 'N/A' ? billTo.city : '',
        billTo.state !== 'N/A' ? billTo.state : '',
        billTo.zip !== 'N/A' ? billTo.zip : '',
      ].filter(Boolean).join(', ');
      if (cityStateZip) {
        billToY += 6;
        doc.text(cityStateZip, 15, billToY);
      }
    }
    if (billTo.contact_phone !== 'N/A') {
      billToY += 6;
      doc.text(`Phone: ${billTo.contact_phone}`, 15, billToY);
    }
    if (billTo.contact_email !== 'N/A') {
      billToY += 6;
      doc.text(`Email: ${billTo.contact_email}`, 15, billToY);
    }
  
    // Ship To
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor);
    doc.text('Ship To:', pageWidth / 2 + 5, 72);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    let shipToY = 78;
    doc.text(shipTo.name, pageWidth / 2 + 5, shipToY);
    if (shipTo.contact_name !== 'N/A') {
      shipToY += 6;
      doc.text(shipTo.contact_name, pageWidth / 2 + 5, shipToY);
    }
    if (shipTo.address !== 'N/A') {
      shipToY += 6;
      doc.text(shipTo.address, pageWidth / 2 + 5, shipToY);
    }
    if (shipTo.city !== 'N/A' && shipTo.state !== 'N/A' && shipTo.zip !== 'N/A') {
      shipToY += 6;
      doc.text(`${shipTo.city}, ${shipTo.state} ${shipTo.zip}`, pageWidth / 2 + 5, shipToY);
    } else {
      const cityStateZip = [
        shipTo.city !== 'N/A' ? shipTo.city : '',
        shipTo.state !== 'N/A' ? shipTo.state : '',
        shipTo.zip !== 'N/A' ? shipTo.zip : '',
      ].filter(Boolean).join(', ');
      if (cityStateZip) {
        shipToY += 6;
        doc.text(cityStateZip, pageWidth / 2 + 5, shipToY);
      }
    }
    if (shipTo.contact_phone !== 'N/A') {
      shipToY += 6;
      doc.text(`Phone: ${shipTo.contact_phone}`, pageWidth / 2 + 5, shipToY);
    }
    if (shipTo.contact_email !== 'N/A') {
      shipToY += 6;
      doc.text(`Email: ${shipTo.contact_email}`, pageWidth / 2 + 5, shipToY);
    }
  
    // Invoice Summary Table
    doc.setFontSize(10);
    const netTerms = deliveryData.net_terms || 15;
    const tableData = [
      [
        deliveryData.po_number || 'N/A',
        `${new Date(createdAt).toLocaleDateString()}`,
        `${netTerms} days`,
        `${new Date(netDueDate).toLocaleDateString()}`,
        `$${deliveryData.total_amount.toFixed(2)}`,
      ],
    ];
  
    autoTable(doc, {
      startY: Math.max(billToY, shipToY) + 10,
      head: [['PO NUMBER', 'DATE', 'NET', 'DUE', 'TOTAL']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2, textColor: [0, 0, 0] },
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 30 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 40 },
      },
    });
  
    // Package Information Table
    // Aggregate package information by package_number
    const packageMap = new Map();
    deliveryData.deliveries.forEach(delivery => {
      if (!packageMap.has(delivery.package_number)) {
        packageMap.set(delivery.package_number, {
          package_number: delivery.package_number,
          ship_date: delivery.ship_date,
          tracking_number: delivery.tracking_number || 'N/A',
          ship_by: delivery.ship_by || 'N/A',
        });
      }
    });
    const packageData = Array.from(packageMap.values()).map(pkg => [
      pkg.package_number,
      new Date(pkg.ship_date).toLocaleDateString(),
      pkg.tracking_number,
      pkg.ship_by,
    ]);
  
    let packageTableY = doc.lastAutoTable.finalY + 0;
    autoTable(doc, {
      startY: packageTableY,
      head: [['PACKAGE NUMBER', 'SHIP DATE', 'TRACKING #', 'SHIP BY']],
      body: packageData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2, textColor: [0, 0, 0] },
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 30 },
        2: { cellWidth: 60 },
        3: { cellWidth: 40 },
      },
    });
  
    // Delivery Items Table
    doc.setFontSize(10);
    const tableDataItems = deliveryData.deliveries.map((delivery) => {
      const unitPrice = delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0;
      return [
        delivery.part_number || 'N/A',
        delivery.description || 'N/A',
        `Line ${delivery.po_line_number}`,
        delivery.quantity,
        `$${unitPrice.toFixed(2)}`,
        `$${(delivery.quantity * unitPrice).toFixed(2)}`,
      ];
    });
  
    let finalY = doc.lastAutoTable.finalY + 5;
    autoTable(doc, {
      startY: finalY,
      head: [['PART', 'DESCRIPTION', 'LINE #', 'QTY', 'UNIT PRICE', 'TOTAL']],
      body: tableDataItems,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2, textColor: [0, 0, 0] },
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { left: 15, right: 15, bottom: 35 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 30 },
      },
      didDrawPage: (data) => {
        finalY = data.cursor.y;
        const madeInAmericaImg = new Image();
        madeInAmericaImg.src = require('../assets/madeInAmerica.png');
        try {
          doc.addImage(madeInAmericaImg, 'PNG', pageWidth - 67.5, pageHeight - 25, 52.5, 15);
        } catch (err) {
          console.error('Error loading Made in America image:', err);
          doc.text('Made in America Missing', pageWidth - 67.5, pageHeight - 25);
        }
      },
    });
  
    // Total Amount
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: $${deliveryData.total_amount.toFixed(2)}`, pageWidth - 15, finalY + 10, { align: 'right' });
  
    // Notes (if any)
    let lastY = finalY + 20;
    if (deliveryData.notes) {
      doc.setFontSize(10);
      doc.setTextColor(primaryColor);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 15, lastY);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(deliveryData.notes, 15, lastY + 6, { maxWidth: pageWidth - 30 });
      lastY += 20;
    }
  
    return doc.output('blob');
  };